/**
 * PWA Offline — OfflineProvider Context
 *
 * Design decisions (from design.md Section 6):
 * - OfflineProvider wraps App below AuthProvider
 * - Provides: isOnline, pendingCount, lastSyncAt
 * - Listens to navigator.onLine and online/offline events
 * - Registers ServiceWorker updates notification
 * - Renders ConflictModal when a sync conflict is detected
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { db } from './db';
import { ConflictModal } from './components/ConflictModal';
import { useOfflineSync } from './hooks/useOfflineSync';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface OfflineContextValue {
    isOnline: boolean;
    pendingCount: number;
    lastSyncAt: number | null;
}

export interface OfflineContextActions {
    refreshPendingCount: () => Promise<void>;
    updateLastSyncAt: (timestamp: number) => Promise<void>;
}

// ── Context ───────────────────────────────────────────────────────────────────

const OfflineContext = createContext<OfflineContextValue & OfflineContextActions | null>(null);

export function useOffline(): OfflineContextValue & OfflineContextActions {
    const context = useContext(OfflineContext);
    if (!context) {
        throw new Error('useOffline must be used within an OfflineProvider');
    }
    return context;
}

// ── Provider ──────────────────────────────────────────────────────────────────

interface OfflineProviderProps {
    children: ReactNode;
}

export function OfflineProvider({ children }: OfflineProviderProps) {
    const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
    const [lastSyncAt, setLastSyncAt] = useState<number | null>(null);
    const {
        sync,
        pendingCount,
        conflict,
        onKeepServer,
        onKeepLocal,
        clearConflict,
    } = useOfflineSync();

    // ── Fetch last sync timestamp from IndexedDB ───────────────────────────

    const fetchLastSyncAt = useCallback(async () => {
        try {
            const meta = await db.sync_meta.get('last_sync_timestamp');
            if (meta) {
                setLastSyncAt(meta.value);
            }
        } catch (error) {
            console.error('[OfflineProvider] Failed to fetch lastSyncAt:', error);
        }
    }, []);

    // ── Update last sync timestamp ─────────────────────────────────────────

    const updateLastSyncAt = useCallback(async (timestamp: number) => {
        try {
            await db.sync_meta.put({ key: 'last_sync_timestamp', value: timestamp });
            setLastSyncAt(timestamp);
        } catch (error) {
            console.error('[OfflineProvider] Failed to update lastSyncAt:', error);
        }
    }, []);

    // ── Refresh pending count (passthrough — owned by useOfflineSync) ───────

    const refreshPendingCount = useCallback(async () => {
        // Owned by useOfflineSync hook — this is a no-op passthrough for useOffline()
    }, []);

    // ── Initialize on mount ───────────────────────────────────────────────

    useEffect(() => {
        fetchLastSyncAt();
    }, [fetchLastSyncAt]);

    // ── Online/offline event listeners ────────────────────────────────────

    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            // Auto-trigger sync when coming back online
            sync();
        };
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [sync]);

    // ── ServiceWorker update detection ─────────────────────────────────────

    useEffect(() => {
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            const handleUpdateFound = () => {
                console.log('[OfflineProvider] ServiceWorker update found');
                sync();
            };

            navigator.serviceWorker.addEventListener('updatefound', handleUpdateFound);

            return () => {
                navigator.serviceWorker.removeEventListener('updatefound', handleUpdateFound);
            };
        }
    }, [sync]);

    // ── Conflict resolution callbacks ─────────────────────────────────────

    const handleKeepServer = useCallback(
        async (entityType: string, entityId: number) => {
            if (!conflict) return;
            await onKeepServer(conflict.write.id!);
        },
        [conflict, onKeepServer]
    );

    const handleKeepLocal = useCallback(
        async (entityType: string, entityId: number) => {
            if (!conflict) return;
            await onKeepLocal(conflict.write.id!, conflict.write.payload as Record<string, unknown>);
        },
        [conflict, onKeepLocal]
    );

    // ── Build conflict data for ConflictModal ─────────────────────────────

    const conflictData = conflict
        ? {
              entityType: conflict.write.entity_type,
              entityId: conflict.write.entity_id,
              localData: conflict.write.payload as Record<string, unknown>,
              serverData: conflict.serverData,
          }
        : null;

    const value: OfflineContextValue & OfflineContextActions = {
        isOnline,
        pendingCount,
        lastSyncAt,
        refreshPendingCount,
        updateLastSyncAt,
    };

    return (
        <OfflineContext.Provider value={value}>
            {children}
            <ConflictModal
                open={conflict !== null}
                conflict={conflictData}
                onKeepServer={handleKeepServer}
                onKeepLocal={handleKeepLocal}
                onClose={clearConflict}
            />
        </OfflineContext.Provider>
    );
}
